import React, { useEffect, useState, createContext } from "react";
import { Responsive, WidthProvider } from "react-grid-layout";
import { DateTime } from "luxon";
import { Get } from "./Server";
import { getDarkColour, hasHeader, getTitle } from "./Components/Util";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";

import TitleCell from "./Cells/TitleCell";
import IconCell from "./Cells/IconCell";
import IconCell2 from "./Cells/IconCell2";
import AlbertaCell from "./Cells/AlbertaCell";
import GraphCell from "./Cells/GraphCell";
import PriceModal from "./Components/PriceModal";
import EmissionModal from "./Components/EmissionModal";
import SourceModal from "./Components/SourceModal";
import FlowModal from "./Components/FlowModal";
import ErrorModal from "./Components/ErrorModal";

export const Context = createContext();
const ResponsiveGridLayout = WidthProvider(Responsive);

export function DecimalPlaces(value, places) {
  if (places === 0) {
    return Math.round(value);
  } else {
    let temp = Math.pow(10, places);
    return Math.round(value * temp) / temp;
  }
}

export default function App() {
  const [opacity, setOpacity] = useState(0);
  const [data, setData] = useState({});

  const [priceData, setPriceData] = useState([]);
  const [priceAverageData, setPriceAverageData] = useState([]);
  const [showPriceHistory, setShowPriceHistory] = useState(false);

  const [emissionData, setEmissionData] = useState([]);
  const [showEmissionHistory, setShowEmissionHistory] = useState(false);

  const [sourceNuclear, setSourceNuclear] = useState([]);
  const [sourceGeothermal, setSourceGeothermal] = useState([]);
  const [sourceBiomass, setSourceBiomass] = useState([]);
  const [sourceCoal, setSourceCoal] = useState([]);
  const [sourceWind, setSourceWind] = useState([]);
  const [sourceSolar, setSourceSolar] = useState([]);
  const [sourceHydro, setSourceHydro] = useState([]);
  const [sourceGas, setSourceGas] = useState([]);
  const [sourceOil, setSourceOil] = useState([]);
  const [sourceUnknown, setSourceUnknown] = useState([]);
  const [sourceHydroDischarge, setSourceHydroDischarge] = useState([]);
  const [sourceBatteryDischarge, setSourceBatteryDischarge] = useState([]);
  const [showSourceHistory, setShowSourceHistory] = useState(false);

  const [tieBC, setTieBC] = useState([]);
  const [tieSK, setTieSK] = useState([]);
  const [tieMontana, setTieMontana] = useState([]);
  const [demand, setDemand] = useState([]);
  const [production, setProduction] = useState([]);
  const [showFlowHistory, setShowFlowHistory] = useState(false);

  const [error, setError] = useState("");

  const [dimensions, setDimensions] = useState({
    height: window.innerHeight,
    width: window.innerWidth,
  });

  useEffect(
    () => {
      // react to window resize
      function windowResize() {
        setDimensions({
          width: window.innerWidth - 15,
          height: window.innerHeight - 80,
        });
      }
      window.addEventListener("resize", windowResize);
      windowResize();

      // server override?
      if (window.location.hash) {
        let server = window.location.hash.substring(1);
        sessionStorage.setItem("server", server);
        console.log("server override: " + server);
        window.location = window.location.href.split("#")[0];
      }

      // refresh periodically
      setInterval(getData, window.REFRESH_RATE * 60 * 1000);

      // set the title
      document.title = getTitle();

      // load now
      getData();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  // header row is optional
  let y = hasHeader() ? 1 : 0;

  function getData() {
    // fetch the latest snapshot
    Get("griddata/latest", setError).then((response) => {
      //console.log(JSON.stringify(response.data, null, 2));

      // if we received a snapshot
      if (response?.data) {
        // store the snapshot
        setData(response.data);

        // fetch the ten day history
        Get("griddata/days/10", setError).then((response) => {
          //console.log(JSON.stringify(response.data, null, 2));

          // if we received history data
          if (response?.data) {
            // store the pool price history
            setPriceData(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.electricityRate,
                };
              })
            );

            // store the pool price average
            setPriceAverageData(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.avg30day,
                };
              })
            );

            // store the ghg history
            setEmissionData(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.ghgRate,
                };
              })
            );

            // store the source production history
            setSourceNuclear(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.sourceNuclear / 1000,
                };
              })
            );
            setSourceGeothermal(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.sourceGeothermal / 1000,
                };
              })
            );
            setSourceBiomass(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.sourceBiomass / 1000,
                };
              })
            );
            setSourceCoal(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.sourceCoal / 1000,
                };
              })
            );
            setSourceWind(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.sourceWind / 1000,
                };
              })
            );
            setSourceSolar(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.sourceSolar / 1000,
                };
              })
            );
            setSourceHydro(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.sourceHydro / 1000,
                };
              })
            );
            setSourceGas(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.sourceGas / 1000,
                };
              })
            );
            setSourceOil(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.sourceOil / 1000,
                };
              })
            );
            setSourceUnknown(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.sourceUnknown / 1000,
                };
              })
            );
            setSourceHydroDischarge(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.sourceHydroDischarge / 1000,
                };
              })
            );
            setSourceBatteryDischarge(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.sourceBatteryDischarge / 1000,
                };
              })
            );

            // store the tie history
            setTieBC(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.bcTie,
                };
              })
            );
            setTieSK(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.skTie,
                };
              })
            );
            setTieMontana(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.montanaTie,
                };
              })
            );

            // store the demand history
            setDemand(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.demand / 1000,
                };
              })
            );

            // store the production history
            setProduction(
              response.data.map((point) => {
                return {
                  x: point.date,
                  y: point.production / 1000,
                };
              })
            );
          }
        });
        setOpacity(1);
      }
    });
  }

  return (
    <>
      <div id="App" style={{ opacity }}>
        <Context.Provider value={data}>
          <ResponsiveGridLayout
            breakpoints={{ lg: 500 }}
            cols={{ lg: 5 }}
            rowHeight={(dimensions.height - 20) / (8.5 + y)}
          >
            {hasHeader() && (
              <div
                key="TitleBar"
                data-grid={{ x: 0, y: 0, w: 5, h: 1, static: true }}
              >
                <TitleCell date={data.date} />
              </div>
            )}
            <div
              key="Current Pool Price"
              data-grid={{ x: 0, y: y, w: 1, h: 2, static: true }}
            >
              <IconCell
                imageName="Price.svg"
                value={"$" + Math.round(data.electricityRate)}
                colour={getDarkColour()}
                subtitle1="Current Pool Price"
                subtitle2="($/MWh)"
                onClick={() => setShowPriceHistory(true)}
              />
            </div>
            <div
              key="Current Emissions"
              data-grid={{ x: 1, y: y, w: 1, h: 2, static: true }}
            >
              <IconCell
                imageName="Emissions.svg"
                value={data.ghgRate + " kg"}
                colour={getDarkColour()}
                subtitle1="Current Emissions"
                subtitle2="(kg/MWh)"
                onClick={() => setShowEmissionHistory(true)}
              />
            </div>
            <div
              key="Source Graph"
              data-grid={{ x: 0, y: y + 2, w: 2, h: 4, static: true }}
            >
              <GraphCell
                sourceNuclear={data.sourceNuclear}
                sourceGeothermal={data.sourceGeothermal}
                sourceBiomass={data.sourceBiomass}
                sourceCoal={data.sourceCoal}
                sourceWind={data.sourceWind}
                sourceSolar={data.sourceSolar}
                sourceHydro={data.sourceHydro}
                sourceGas={data.sourceGas}
                sourceOil={data.sourceOil}
                sourceUnknown={data.sourceUnknown}
                sourceHydroDischarge={data.sourceHydroDischarge}
                sourceBatteryDischarge={data.sourceBatteryDischarge}
                onClick={() => setShowSourceHistory(true)}
              />
            </div>
            <div
              key="Subscribe"
              data-grid={{ x: 0, y: y + 6, w: 2, h: 2, static: true }}
            >
              <IconCell2
                text1="Subscribe for Pool Price Alerts, price"
                text2="signals, alerts and monthly performance"
                text3="reports"
                imageName="Subscribe.svg"
                subtitle1="Subscribe"
                onClick={() => window.open(window.SUBSCRIBE_URL, "_blank")}
              />
            </div>
            <div
              key="Alberta Energy Flow"
              data-grid={{ x: 2, y: y, w: 2, h: 8, static: true }}
            >
              <AlbertaCell
                demand={data.demand}
                production={data.production}
                bcTie={data.bcTie}
                skTie={data.skTie}
                montanaTie={data.montanaTie}
                onClick={() => setShowFlowHistory(true)}
              />
            </div>
            <div
              key="Reduce your energy costs"
              data-grid={{ x: 4, y: y, w: 1, h: 2, static: true }}
            >
              <IconCell
                imageName="Costs.svg"
                subtitle1="Reduce your"
                subtitle2="energy costs"
              />
            </div>
            <div
              key="Investment Grade Energy Strategy"
              data-grid={{ x: 4, y: y + 2, w: 1, h: 2, static: true }}
            >
              <IconCell
                imageName="Strategy.svg"
                subtitle1="Investment Grade"
                subtitle2="Energy Strategy"
                onClick={() => window.open(window.STRATEGY_URL, "_blank")}
              />
            </div>
            <div
              key="Participate in Electricity Market"
              data-grid={{ x: 4, y: y + 4, w: 1, h: 2, static: true }}
            >
              <IconCell
                imageName="Market.svg"
                subtitle1="Participate in"
                subtitle2="Electricity Market"
                onClick={() =>
                  window.open(window.ELECTRICITY_MARKET_URL, "_blank")
                }
              />
            </div>
            <div
              key="Participate in Operating Reserves"
              data-grid={{ x: 4, y: y + 6, w: 1, h: 2, static: true }}
            >
              <IconCell
                imageName="Reserves.svg"
                subtitle1="Participate in"
                subtitle2="Operating Reserves"
                onClick={() =>
                  window.open(window.OPERATING_RESERVES_URL, "_blank")
                }
              />
            </div>
          </ResponsiveGridLayout>
          {!hasHeader() && (
            <div className="footer">
              {data.date &&
                "Updated: " +
                  DateTime.fromMillis(data.date, {
                    zone: "Canada/Mountain",
                  }).toLocaleString(DateTime.DATETIME_FULL)}
            </div>
          )}
        </Context.Provider>
      </div>
      <PriceModal
        show={showPriceHistory}
        close={() => setShowPriceHistory(false)}
        priceData={priceData}
        priceAverageData={priceAverageData}
        demand={demand}
        height={dimensions.height * 0.8}
      />
      <EmissionModal
        show={showEmissionHistory}
        close={() => setShowEmissionHistory(false)}
        emissionData={emissionData}
        height={dimensions.height * 0.8}
      />
      <SourceModal
        show={showSourceHistory}
        close={() => setShowSourceHistory(false)}
        sourceNuclear={sourceNuclear}
        sourceGeothermal={sourceGeothermal}
        sourceBiomass={sourceBiomass}
        sourceCoal={sourceCoal}
        sourceWind={sourceWind}
        sourceSolar={sourceSolar}
        sourceHydro={sourceHydro}
        sourceGas={sourceGas}
        sourceOil={sourceOil}
        sourceUnknown={sourceUnknown}
        sourceHydroDischarge={sourceHydroDischarge}
        sourceBatteryDischarge={sourceBatteryDischarge}
        height={dimensions.height * 0.8}
      />
      <FlowModal
        show={showFlowHistory}
        close={() => setShowFlowHistory(false)}
        tieBC={tieBC}
        tieSK={tieSK}
        tieMontana={tieMontana}
        demand={demand}
        production={production}
        height={dimensions.height * 0.8}
      />
      <ErrorModal
        show={error !== ""}
        close={() => setError("")}
        error={error.error}
        height={window.innerHeight * 0.5}
      />
    </>
  );
}
